import { DownloadIcon, Eye } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'
import { tv, type VariantProps } from 'tailwind-variants'

import { Button } from '@/components/button'
import { LoaderCircle } from '@/components/loader-circle'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/tooltip'
import api, { BASE_URL } from '@/services/api'
import { downloadFile } from '@/utils/download-file'

const downloadButtonVariants = tv({
  base: 'max-h-8 p-2',
  variants: {
    variant: {
      default: '',
      bounded:
        'max-h-max bg-bios-primary !px-3 !py-2 text-white transition-all duration-300 hover:bg-bios-secondary',
      underline: 'text-bios-secondary',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

interface DownloadButtonProps
  extends React.HTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof downloadButtonVariants> {
  downloadRoute: string
  directDownload?: boolean
  tooltipText?: string
  notFoundError?: string
  isCanceledProperty?: boolean
  blobReturn?: boolean
  text?: string
  isPerimeterDownload?: boolean
  testId: string
}

export function DownloadButton({
  downloadRoute,
  className,
  variant,
  text,
  directDownload = false,
  tooltipText = 'Visualizar certificado',
  notFoundError,
  isCanceledProperty = false,
  blobReturn = false,
  isPerimeterDownload = false,
  testId,
}: DownloadButtonProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  async function handleDownload() {
    setIsLoading(true)

    let route = downloadRoute
    if (downloadRoute.startsWith(BASE_URL)) {
      route = downloadRoute.replace(BASE_URL, '')
    }

    const requestConfig =
      blobReturn || isPerimeterDownload ? { responseType: 'blob' as const } : {}

    await api
      .get(route, requestConfig)
      .then(({ data, headers }) => {
        if (!data) throw new Error('No data')

        const filename: string | undefined =
          headers['content-disposition']
            ?.split('filename=')[1]
            ?.replace(/"/g, '') ||
          route.split('/').pop()?.split('?')[0] ||
          'document.pdf'

        if (blobReturn) {
          downloadFile({ blob: data, filename, viewFirst: !directDownload })
          return
        }

        if (isPerimeterDownload) {
          const contentType = headers['content-type']

          const blob = new Blob([data], { type: contentType })

          downloadFile({ blob, filename, viewFirst: !directDownload })
          return
        }

        downloadFile({ url: data, filename, viewFirst: !directDownload })
      })
      .catch((error) => {
        if (error.response?.status === 500) {
          toast.error(
            error.response.data.message ||
              (directDownload
                ? 'Erro ao baixar o arquivo'
                : 'Erro ao abrir o documento'),
          )
          return
        }
        toast.warning(error.response?.data?.message || notFoundError)
      })
      .finally(() => setIsLoading(false))
  }

  function UnderlineText({ text }: { text: string }) {
    if (variant !== 'underline') return null

    return (
      <span className="text-bios-secondary text-lg font-bold underline">
        {text}
      </span>
    )
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid={testId}
            variant="ghost"
            disabled={downloadRoute === '' || isCanceledProperty}
            className={twMerge(
              downloadButtonVariants({ variant, className }),
              variant === 'underline' &&
                'flex items-center gap-1.5 border-0 p-0 hover:bg-transparent',
            )}
            onClick={(e) => {
              e.stopPropagation()
              handleDownload()
            }}
          >
            {isLoading ? (
              <LoaderCircle />
            ) : directDownload ? (
              <>
                <DownloadIcon />

                <UnderlineText text={text ?? ''} />
              </>
            ) : (
              <>
                <Eye />

                <UnderlineText text={text ?? ''} />
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
